import { NullableChartPeriod } from "./CleanChart.control";
import { ChartLabelsAndData } from "./HashChart";

function getStatisticsDiscrete(daysCount: number): number {
  if (daysCount > 3) {
    return 60;
  }
  if (daysCount > 1) {
    return 30;
  }
  return 5;
}

function getDiscreteByPeriodInMins(
  period: NullableChartPeriod
): number {
  if (period.length > 1 && period[0] !== null && period[1] !== null) {
    const diffDays = period[1].diff(period[0], "days");
    return getStatisticsDiscrete(diffDays);
  }
  return 5;
}

function getNearestCeilDividableTime(
  time: number,
  period: number
): number {
  return Math.ceil(time / period) * period;
}

function createCalculatedDates(
  lastTime: number | null,
  newTime: number,
  deltaInMs: number
): number[] {
  // 8:10, 8:14:12 -> 8:10, 8:14:12 (temp) = append temo
  // 8:10, 8:15:13 -> 8:10, 8:15(c), 8:15:13 (temp)
  // 8:10, 8:16:14 -> 8:10, 8:15(c), 8:16:14 (temp)
  // 8:10, 8:19:11 -> 8:10, 8:15(c), 8:19:11 (temp)
  // 8:10, 8:20:15 -> 8:10, 8:15(c), 8:20, 8:20:15
  // 8:10, 8:30:16 -> refresh? or 8:15(c) 8:20(c) 8:25(c) 8:30(c) 8:30:16
  // 8:09:01, 8:10:13 -> 8:10, 8:10:13
  const result: number[] = [];
  if (lastTime !== null && lastTime > 0) {
    const nextDividable = getNearestCeilDividableTime(
      Math.floor(lastTime / 1000),
      Math.floor(deltaInMs / 1000)
    ) * 1000;
    if (nextDividable < newTime && nextDividable > lastTime) {
      result.push(nextDividable);
      lastTime = nextDividable;
    }
    const n5delta = Math.floor((newTime - lastTime) / deltaInMs);
    for (let i = 0; i < n5delta; i++) {
      result.push(lastTime + deltaInMs * (i + 1));
    }
  }
  result.push(newTime);
  return result;
}

function createAvgValues(
  newValue: number,
  datesCount: number
): number[] {
  const result: number[] = [];
  // TODO: возможно добавить вычисление по пропорции
  for (let i = 0; i < datesCount - 1; i++) {
    result.push(newValue);
  }
  result.push(newValue);
  return result;
}

function getCoordX(
  x1: number, y1: number,
  x2: number, y2: number,
  y: number
): number {
  // A(x₁, y₁), B(x₂, y₂), C(?, y)
  return (y - y1) * (x2 - x1) / (y2 - y1) + x1;
}

function createBoundsValue(
  hash: number, // y2
  date: number, // x2
  prevHash: number, // y1
  prevDate: number, // x1
  speedDefault: number,
  speedDrops: number
): [number, number] | null {
  if (date - prevDate < 30000)
    return null;
  const defaultSpeedReal = speedDefault * 0.95;
  if (prevHash < hash) { // up
    if (prevHash < speedDrops && hash > speedDrops) {
      return [speedDrops, getCoordX(prevDate, prevHash, date, hash, speedDrops)];
    }
    if (prevHash < defaultSpeedReal && hash > defaultSpeedReal) {
      return [defaultSpeedReal, getCoordX(prevDate, prevHash, date, hash, defaultSpeedReal)];
    }
  }
  if (prevHash > hash) { // down
    if (prevHash > defaultSpeedReal && hash < defaultSpeedReal) {
      return [defaultSpeedReal, getCoordX(prevDate, prevHash, date, hash, defaultSpeedReal)];
    }
    if (prevHash > speedDrops && hash < speedDrops) {
      return [speedDrops, getCoordX(prevDate, prevHash, date, hash, speedDrops)];
    }
  }
  return null;
}

function appendBounds(
  value: ChartLabelsAndData,
  speedDefault: number,
  speedDrops: number,
  lastTime: number | null = null,
  lastValue: number | null = null,
): ChartLabelsAndData {
  const result: ChartLabelsAndData = {
    labels: [],
    data: [],
    status: [],
    accepted: [],
    rejected: [],
  };
  const hasLastValue = (lastTime !== null && lastValue !== null && lastTime > 0);
  if (value && value.data.length > 0 && value.labels.length > 0) {
    if (hasLastValue) {
      value.data.unshift(lastValue);
      value.labels.unshift(lastTime);
    }
    let prevHash = value.data[0];
    let prevTime = value.labels[0];
    result.data.push(prevHash);
    result.labels.push(prevTime);

    try {
      let prevAccepted = value.accepted[0];
      let prevRejected = value.rejected[0];
      result.accepted.push(prevAccepted);
      result.rejected.push(prevRejected);
    } catch { // на случай отсутствия accept/reject
    }

    for (let i = 1; i < value.data.length; i++) {
      const bound = createBoundsValue(
        value.data[i],
        value.labels[i],
        prevHash,
        prevTime,
        speedDefault,
        speedDrops
      );
      if (bound) {
        result.data.push(bound[0]);
        result.labels.push(bound[1]);
        if (result.accepted.length > 0) {
          result.accepted.push(result.accepted[result.accepted.length - 1]);
        }
        if (result.rejected.length > 0) {
          result.rejected.push(result.rejected[result.rejected.length - 1]);
        }
        // чтобы добавить еще одну границу, если перепад большой
        prevHash = bound[0];
        prevTime = bound[1];
        i--;
        continue;
      }
      result.data.push(value.data[i]);
      result.labels.push(value.labels[i]);
      try {
        result.accepted.push(value.accepted[i]);
        result.rejected.push(value.rejected[i]);
      } catch { }
      prevHash = value.data[i];
      prevTime = value.labels[i];
    }
    if (hasLastValue) {
      result.data.shift();
      result.labels.shift();
      try {
        result.accepted.shift();
        result.rejected.shift();
      } catch { }
    }
  }
  return result;
}

function transformResponseToChartData(
  h: number[][],
): ChartLabelsAndData {
  const result: ChartLabelsAndData = {
    labels: [],
    data: [],
    status: [],
    accepted: [],
    rejected: [],
  };
  if (h.length === 0) {
    return result;
  }
  const [l, d] = h;
  if (!!l && !!d) {
    for (let i = 0; i < l.length; i++) {
      result.labels.push(l[i] * 1000);
      result.data.push(d[i]);
    }
  }
  return result;
}

export interface HashrateItem {
  status: number;
  speed: number;
  accepted: number;
  rejected: number;
  timestamp: number;
}

// 16.07.2026 02:10
function transformResponseToChartData2(
  h: HashrateItem[]
): ChartLabelsAndData {
  const result: ChartLabelsAndData = {
    labels: [],
    data: [],

    status: [],
    accepted: [],
    rejected: [],
  };
  for (let i = 0; i < h.length; i++) {
    result.labels.push(h[i].timestamp * 1000);
    result.data.push(h[i].speed);
    ///
    result.status.push(h[i].status);
    result.accepted.push(h[i].accepted);
    result.rejected.push(h[i].rejected);
  }
  // const dt = Date.UTC(2026, 6, 16, 2, 10);
  // const filterDate = Math.floor(dt / 1000); // 16.07.2026 02:10

  // for (let i = 0; i < h.length; i++) {
  //   if (h[i].timestamp < filterDate) {
  //     //continue;
  //     result.labels.push(h[i].timestamp * 1000);
  //     result.data.push(NaN);
  //     ///
  //     result.status.push(NaN);
  //     result.accepted.push(NaN);
  //     result.rejected.push(NaN);
  //   } else {
  //     result.labels.push(h[i].timestamp * 1000);
  //     result.data.push(h[i].speed);
  //     ///
  //     result.status.push(h[i].status);
  //     result.accepted.push(h[i].accepted);
  //     result.rejected.push(h[i].rejected);
  //   }
  // }
  return result;
}

const ChartDatasetUtils = {
  getStatisticsDiscrete,
  createCalculatedDates,
  createAvgValues,
  appendBounds,
  transformResponseToChartData,
  transformResponseToChartData2,
  getDiscreteByPeriodInMins,
  getNearestCeilDividableTime,
};

export default ChartDatasetUtils;
